import React, { useContext } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { TextInput, SelectBox, FileUploader } from '../form';
import { useForm } from '../../hooks';
import { DialogContext, SnackbarContext } from '../../contexts';
import Handler from '../../Handler';
import API from '../../api';

// ITS-200. 방법론 관련 문서(등록부 문서 · 공개논평 draft) 목록 및 추가/삭제.
const DOC_TYPE_LABELS = {
  BASIC: '기본 문서 (등록부)',
  RESULT: '결과 문서 (등록부)',
  CONSULTATION: '공개논평 문서 (draft)',
};

const fileNameOf = (url) => (url ? url.split('/')[url.split('/').length - 1] : '');

export const MethodologyDocuments = (props) => {
  const { seq, documents, onChange, canEdit } = props;
  const { updateDialog } = useContext(DialogContext);
  const { updateSnackbar } = useContext(SnackbarContext);

  // ITS-200(리뷰 반영). FileUploader 는 meta.key 로 DOM input id 를 만드므로(FileUploader.jsx:91,132,159),
  // 기본정보 폼의 attachedFile 과 같은 키를 쓰면 같은 화면에서 id 가 충돌한다. docFile/docFileEn 으로 분리.
  const [formData, formMeta, formChange, , , formReset] = useForm(
    {
      type: 'BASIC',
      title: '',
      titleEn: '',
      docFile: '',
      docFileEn: '',
    },
    {
      type: {
        key: 'type',
        label: '유형',
        fullWidth: true,
        required: true,
        menus: [
          { value: 'BASIC', label: DOC_TYPE_LABELS.BASIC },
          { value: 'RESULT', label: DOC_TYPE_LABELS.RESULT },
          { value: 'CONSULTATION', label: DOC_TYPE_LABELS.CONSULTATION },
        ],
      },
      title: {
        key: 'title',
        label: '제목',
        fullWidth: true,
        required: true,
      },
      titleEn: {
        key: 'titleEn',
        label: '제목 (영문)',
        fullWidth: true,
        required: true,
      },
      docFile: {
        key: 'docFile',
        label: '파일',
        type: 'file',
        path: 'methodology/relatedDocument',
        required: true,
      },
      docFileEn: {
        key: 'docFileEn',
        label: '파일 (영문)',
        type: 'file',
        path: 'methodology/relatedDocument',
        required: true,
      },
    },
  );

  const requestDelete = (doc) => {
    updateDialog({
      type: 'error',
      open: true,
      title: '문서 삭제',
      description: `${doc.title}\n문서를 삭제하시겠습니까?`,
      onConfirm: () => {
        API.Methodology.DeleteDocument(seq, doc.seq).then(
          () => {
            onChange(documents.filter((item) => item.seq !== doc.seq));
            updateSnackbar({
              type: 'success',
              open: true,
              message: '문서가 삭제되었습니다.',
            });
          },
          (err) => {
            updateSnackbar({
              type: 'error',
              open: true,
              message: err.data.message,
            });
          },
        );
      },
    });
  };

  const requestAdd = () => {
    let body = Handler.formToBody(formData, formMeta);
    if (!body) {
      updateSnackbar({
        type: 'error',
        open: true,
        message: '필수정보를 확인하세요',
      });
      return;
    }
    const requestBody = {
      type: body.type,
      title: body.title,
      titleEn: body.titleEn,
      attachedFile: body.docFile,
      attachedFileEn: body.docFileEn,
    };
    API.Methodology.PostDocument(seq, requestBody).then(
      (res) => {
        onChange([...documents, res.data]);
        formReset();
        updateSnackbar({
          type: 'success',
          open: true,
          message: '문서가 추가되었습니다.',
        });
      },
      (err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  };

  return (
    <article className='card-methodology-documents'>
      <article className='custom-table'>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>유형</TableCell>
                <TableCell>제목</TableCell>
                <TableCell>영문 제목</TableCell>
                <TableCell>파일</TableCell>
                <TableCell>영문 파일</TableCell>
                {canEdit && <TableCell>삭제</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(!documents || documents.length === 0) && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5}>
                    <span>등록된 문서가 없습니다.</span>
                  </TableCell>
                </TableRow>
              )}
              {documents &&
                documents.map((doc) => (
                  <TableRow key={'doc-' + doc.seq}>
                    <TableCell>{DOC_TYPE_LABELS[doc.type] || doc.type}</TableCell>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>{doc.titleEn}</TableCell>
                    <TableCell>
                      {doc.attachedFile && (
                        <a href={doc.attachedFile} target='_blank' rel='noreferrer'>
                          {fileNameOf(doc.attachedFile)}
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.attachedFileEn && (
                        <a href={doc.attachedFileEn} target='_blank' rel='noreferrer'>
                          {fileNameOf(doc.attachedFileEn)}
                        </a>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <IconButton onClick={() => requestDelete(doc)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </article>
      {canEdit && (
        <div className='flex' style={{ marginTop: 20 }}>
          <SelectBox value={formData.type} onChange={formChange} meta={formMeta.type}></SelectBox>
          <TextInput value={formData.title} onChange={formChange} meta={formMeta.title}></TextInput>
          <TextInput value={formData.titleEn} onChange={formChange} meta={formMeta.titleEn}></TextInput>
          <FileUploader value={formData.docFile} onChange={formChange} meta={formMeta.docFile}></FileUploader>
          <FileUploader value={formData.docFileEn} onChange={formChange} meta={formMeta.docFileEn}></FileUploader>
          <Button variant='contained' onClick={requestAdd}>
            문서 추가
          </Button>
        </div>
      )}
    </article>
  );
};
